/**
 * Cartoon Simulation Configuration
 *
 * Modern 3D cartoon miniature scene styling inspired by Apple's design language.
 * Uses soft, refined textures with PBR-style materials and gentle lighting.
 */

// ===========================================
// CANVAS CONFIGURATION
// ===========================================

export const CARTOON_CANVAS = {
  WIDTH: 1080,
  HEIGHT: 1080,
  HEADER_HEIGHT: 140,
  SCENE_TOP: 150,
  SCENE_HEIGHT: 930,
  ASPECT_RATIO: 1,
} as const;

// ===========================================
// COLOR PALETTE (Apple-Inspired)
// ===========================================

export const CARTOON_COLORS = {
  // Background
  background: {
    primary: '#F5F5F7',
    secondary: '#FBFBFD',
    gradient: ['#FBFBFD', '#E8E8ED'] as [string, string],
  },

  // Header
  header: {
    title: '#1D1D1F',
    subtitle: '#86868B',
    accent: '#007AFF',
    weatherBg: 'rgba(255, 255, 255, 0.9)',
  },

  // Building/Office
  building: {
    wall: '#E8E8ED',
    wallLight: '#F5F5F7',
    wallShadow: '#D2D2D7',
    glass: 'rgba(147, 197, 253, 0.5)',
    glassReflect: 'rgba(255, 255, 255, 0.3)',
    floor: '#F0EDE8',
    floorPattern: '#E8E4DF',
    carpet: '#4A6FA5',
    carpetPattern: '#3D5C8A',
  },

  // Furniture (Soft, rounded)
  furniture: {
    deskWood: '#C4A484',
    deskWoodLight: '#D4B896',
    deskWoodDark: '#A08060',
    deskMetal: '#E0E0E0',
    chair: '#1D1D1F',
    chairSeat: '#007AFF',
    chairSeatLight: '#4DA3FF',
    plant: '#34C759',
    plantLight: '#5DD87A',
    plantDark: '#248A3D',
    plantPot: '#A0522D',
    plantPotLight: '#CD853F',
    coffee: '#4A5568',
    coffeeAccent: '#718096',
  },

  // Agents (Cartoon style)
  agents: {
    skin: '#FFD7BE',
    skinShadow: '#E8C4A8',
    hair: {
      dark: '#374151',
      brown: '#92400E',
      blonde: '#D97706',
    },
    coordinator: {
      primary: '#007AFF',
      secondary: '#0056B3',
      accent: '#B3D7FF',
      glow: 'rgba(0, 122, 255, 0.4)',
    },
    delegator: {
      primary: '#34C759',
      secondary: '#248A3D',
      accent: '#B8F0C8',
      glow: 'rgba(52, 199, 89, 0.4)',
    },
    worker: {
      primary: '#FF9500',
      secondary: '#CC7700',
      accent: '#FFD9A8',
      glow: 'rgba(255, 149, 0, 0.4)',
    },
  },

  // Screens (Dark mode aesthetic)
  screens: {
    frame: '#2D2D2D',
    frameDark: '#1D1D1F',
    bezel: '#3A3A3C',
    screen: '#0D0D0D',
    screenGlow: 'rgba(255, 255, 255, 0.05)',
    kpi: {
      accent: '#007AFF',
      excellent: '#34C759',
      good: '#007AFF',
      warning: '#FF9500',
      critical: '#FF3B30',
    },
    alarm: {
      accent: '#FF3B30',
      accentGlow: 'rgba(255, 59, 48, 0.6)',
      safe: '#34C759',
    },
    recommend: {
      accent: '#AF52DE',
      accentLight: '#D4A5F5',
    },
  },

  // Shadows
  shadows: {
    soft: 'rgba(0, 0, 0, 0.12)',
    medium: 'rgba(0, 0, 0, 0.18)',
    strong: 'rgba(0, 0, 0, 0.25)',
  },

  // Weather icons background
  weather: {
    sunny: { bg: '#FEF3C7', icon: '#F59E0B', text: '#92400E' },
    partly: { bg: '#DBEAFE', icon: '#3B82F6', text: '#1E40AF' },
    cloudy: { bg: '#F3F4F6', icon: '#6B7280', text: '#374151' },
    rainy: { bg: '#EDE9FE', icon: '#8B5CF6', text: '#5B21B6' },
    stormy: { bg: '#FEE2E2', icon: '#EF4444', text: '#991B1B' },
  },
} as const;

// ===========================================
// LIGHTING CONFIGURATION (PBR-Style)
// ===========================================

export const LIGHTING_CONFIG = {
  // Main directional light (simulates sun)
  mainLight: {
    angle: 315, // degrees (top-left)
    intensity: 0.85,
    color: '#FFFFFF',
  },

  // Fill light (softer, opposite side)
  fillLight: {
    angle: 135,
    intensity: 0.3,
    color: '#E8F4FF',
  },

  // Ambient light
  ambient: {
    intensity: 0.25,
    color: '#F0F0F5',
  },

  // Shadow configuration
  shadow: {
    offsetX: 6,
    offsetY: 6,
    blur: 12,
    color: 'rgba(0, 0, 0, 0.15)',
  },

  // Soft shadow for elevated objects
  elevatedShadow: {
    offsetX: 8,
    offsetY: 10,
    blur: 20,
    color: 'rgba(0, 0, 0, 0.2)',
  },
} as const;

// ===========================================
// TIME OF DAY MODIFIERS
// ===========================================

export type TimeOfDay = 'DAWN' | 'MORNING' | 'MIDDAY' | 'AFTERNOON' | 'DUSK' | 'EVENING' | 'NIGHT';

export interface TimeLighting {
  tint: string;
  tintOpacity: number;
  intensity: number;
  skyGradient: [string, string];
  ambientColor: string;
  shadowIntensity: number;
}

export const TIME_LIGHTING: Record<TimeOfDay, TimeLighting> = {
  DAWN: {
    tint: '#FFE4C4',
    tintOpacity: 0.15,
    intensity: 0.85,
    skyGradient: ['#FFD89B', '#87CEEB'],
    ambientColor: 'rgba(255, 200, 150, 0.2)',
    shadowIntensity: 0.12,
  },
  MORNING: {
    tint: '#FFFAF0',
    tintOpacity: 0.08,
    intensity: 0.95,
    skyGradient: ['#87CEEB', '#E0F4FF'],
    ambientColor: 'rgba(255, 255, 240, 0.15)',
    shadowIntensity: 0.15,
  },
  MIDDAY: {
    tint: '#FFFFFF',
    tintOpacity: 0,
    intensity: 1.0,
    skyGradient: ['#4DA6FF', '#87CEEB'],
    ambientColor: 'rgba(255, 255, 255, 0.1)',
    shadowIntensity: 0.18,
  },
  AFTERNOON: {
    tint: '#FFF8DC',
    tintOpacity: 0.1,
    intensity: 0.92,
    skyGradient: ['#87CEEB', '#FFE4B5'],
    ambientColor: 'rgba(255, 248, 220, 0.15)',
    shadowIntensity: 0.2,
  },
  DUSK: {
    tint: '#FFB6C1',
    tintOpacity: 0.2,
    intensity: 0.8,
    skyGradient: ['#FF7F50', '#FFB6C1'],
    ambientColor: 'rgba(255, 150, 150, 0.2)',
    shadowIntensity: 0.25,
  },
  EVENING: {
    tint: '#4169E1',
    tintOpacity: 0.25,
    intensity: 0.65,
    skyGradient: ['#4B0082', '#483D8B'],
    ambientColor: 'rgba(65, 105, 225, 0.25)',
    shadowIntensity: 0.3,
  },
  NIGHT: {
    tint: '#191970',
    tintOpacity: 0.35,
    intensity: 0.45,
    skyGradient: ['#0D0D2B', '#1A1A4E'],
    ambientColor: 'rgba(25, 25, 112, 0.35)',
    shadowIntensity: 0.1,
  },
};

// ===========================================
// WEATHER / SENTIMENT MAPPING
// ===========================================

export type WeatherSentiment = 'sunny' | 'partly' | 'cloudy' | 'rainy' | 'stormy';

export interface MarketSentiment {
  score: number;
  trend: number;
  direction: 'up' | 'down' | 'stable';
  weather: WeatherSentiment;
}

export const WEATHER_ICONS: Record<WeatherSentiment, string> = {
  sunny: '☀️',
  partly: '🌤️',
  cloudy: '☁️',
  rainy: '🌧️',
  stormy: '⛈️',
};

export const WEATHER_LABELS: Record<WeatherSentiment, string> = {
  sunny: 'Excellent',
  partly: 'Good',
  cloudy: 'Moderate',
  rainy: 'Challenging',
  stormy: 'Critical',
};

/**
 * Calculate market sentiment from KPI scores
 */
export function calculateMarketSentiment(kpis: Array<{ value: number; target: number; trend: 'UP' | 'DOWN' | 'STABLE' }>): MarketSentiment {
  if (kpis.length === 0) {
    return { score: 50, trend: 0, direction: 'stable', weather: 'cloudy' };
  }

  // Calculate average score
  const totalScore = kpis.reduce((sum, kpi) => {
    const pct = Math.min(100, (kpi.value / kpi.target) * 100);
    return sum + pct;
  }, 0);
  const avgScore = totalScore / kpis.length;

  // Calculate trend
  const upCount = kpis.filter(k => k.trend === 'UP').length;
  const downCount = kpis.filter(k => k.trend === 'DOWN').length;
  const trendValue = ((upCount - downCount) / kpis.length) * 10;

  // Map to weather
  let weather: WeatherSentiment;
  if (avgScore >= 80) weather = 'sunny';
  else if (avgScore >= 60) weather = 'partly';
  else if (avgScore >= 40) weather = 'cloudy';
  else if (avgScore >= 20) weather = 'rainy';
  else weather = 'stormy';

  return {
    score: Math.round(avgScore),
    trend: Math.round(trendValue * 10) / 10,
    direction: trendValue > 0.5 ? 'up' : trendValue < -0.5 ? 'down' : 'stable',
    weather,
  };
}

// ===========================================
// ANIMATION CONFIGURATION
// ===========================================

export const ANIMATION_CONFIG = {
  // Agent animation
  agent: {
    walkSpeed: 0.15,
    bobAmplitude: 3,
    bobFrequency: 0.3,
    legSwingAmplitude: 4,
    legSwingFrequency: 0.6,
  },

  // Activity progress
  activity: {
    baseProgressRate: 1.5,
    progressVariance: 1.0,
  },

  // Screen animations
  screen: {
    pulseFrequency: 0.1,
    glowIntensityMin: 0.6,
    glowIntensityMax: 1.0,
  },

  // Particle effects (optional)
  particles: {
    coffeesteamCount: 3,
    steamRiseSpeed: 0.5,
  },
} as const;

// ===========================================
// GRID CONFIGURATION (Updated for square canvas)
// ===========================================

export const CARTOON_GRID = {
  TILE_WIDTH: 54,
  TILE_HEIGHT: 27,
  GRID_COLS: 16,
  GRID_ROWS: 14,
  ORIGIN_X: 540, // Center of 1080
  ORIGIN_Y: 200, // Below header
} as const;

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Convert isometric grid coordinates to screen coordinates
 */
export function isoToScreen(
  isoX: number,
  isoY: number,
  originX: number = CARTOON_GRID.ORIGIN_X,
  originY: number = CARTOON_GRID.ORIGIN_Y
): { x: number; y: number } {
  const { TILE_WIDTH, TILE_HEIGHT } = CARTOON_GRID;
  return {
    x: originX + (isoX - isoY) * (TILE_WIDTH / 2),
    y: originY + (isoX + isoY) * (TILE_HEIGHT / 2),
  };
}

/**
 * Calculate render depth for sorting
 */
export function calculateDepth(isoX: number, isoY: number, elevation: number = 0): number {
  return isoX + isoY + elevation * 0.1;
}

/**
 * Draw a rounded rectangle (polyfill for older browsers)
 */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Create a gradient for 3D effect
 */
export function create3DGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  lightColor: string,
  darkColor: string
): CanvasGradient {
  const gradient = ctx.createRadialGradient(
    x - radius * 0.3,
    y - radius * 0.3,
    0,
    x,
    y,
    radius
  );
  gradient.addColorStop(0, lightColor);
  gradient.addColorStop(1, darkColor);
  return gradient;
}

/**
 * Apply soft shadow to context
 */
export function applySoftShadow(
  ctx: CanvasRenderingContext2D,
  offsetX: number = LIGHTING_CONFIG.shadow.offsetX,
  offsetY: number = LIGHTING_CONFIG.shadow.offsetY,
  blur: number = LIGHTING_CONFIG.shadow.blur,
  color: string = LIGHTING_CONFIG.shadow.color
): void {
  ctx.shadowOffsetX = offsetX;
  ctx.shadowOffsetY = offsetY;
  ctx.shadowBlur = blur;
  ctx.shadowColor = color;
}

/**
 * Clear shadow from context
 */
export function clearShadow(ctx: CanvasRenderingContext2D): void {
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';
}

/**
 * Get time of day from hour
 */
export function getTimeOfDay(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 7) return 'DAWN';
  if (hour >= 7 && hour < 11) return 'MORNING';
  if (hour >= 11 && hour < 14) return 'MIDDAY';
  if (hour >= 14 && hour < 17) return 'AFTERNOON';
  if (hour >= 17 && hour < 19) return 'DUSK';
  if (hour >= 19 && hour < 22) return 'EVENING';
  return 'NIGHT';
}

/**
 * Get lighting for current time
 */
export function getLightingForTime(time: Date): TimeLighting {
  const hour = time.getHours();
  const timeOfDay = getTimeOfDay(hour);
  return TIME_LIGHTING[timeOfDay];
}
